import React, { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Search, Barcode, Camera, PenLine, Salad, X, Bookmark, Plus, Trash2, ScanLine, Zap, Star, Upload, Sparkles } from "lucide-react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { DecodeHintType, BarcodeFormat } from "@zxing/library";
import api from "@/lib/api";
import TimeField from "@/components/TimeField";
import { toast } from "sonner";

const MEALS = ["breakfast", "lunch", "dinner", "snack"];

// Athlete-local "HH:MM" right now — stamped on every food log.
const nowHHMM = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};
const TABS = [
  { id: "plan", label: "My Plan", icon: Salad },
  { id: "favourites", label: "Favourites", icon: Star },
  { id: "recent", label: "Recent", icon: Zap },
  { id: "meals", label: "My Meals", icon: Bookmark },
  { id: "search", label: "Search", icon: Search },
  { id: "scans", label: "Scans", icon: ScanLine },
  { id: "barcode", label: "Barcode", icon: Barcode },
  { id: "custom", label: "Custom", icon: PenLine },
  { id: "photo", label: "Photo AI", icon: Camera },
  { id: "describe", label: "Describe", icon: Sparkles },
];

const scale = (per100, grams) => Math.round(((per100 || 0) * grams) / 100 * 10) / 10;
const r1 = (x) => Math.round((x || 0) * 10) / 10;
const foodKey = (f) => String((f && (f.code || (f.name || "").toLowerCase())) || "");

export default function FoodLogDialog({ open, onOpenChange, date, meal, planMeals = [], onAdded, initialTab, initialBuildItems, onEditPlanMeal }) {
  const [tab, setTab] = useState("plan");
  const [mealSel, setMealSel] = useState(meal || "breakfast");
  const [saving, setSaving] = useState(false);
  const [attach, setAttach] = useState({ path: null, preview: null, busy: false });
  // Local clock time of the meal — auto-stamped "now" when the dialog opens,
  // editable so athletes logging later can correct it. Powers Insights →
  // Eating Times.
  const [logTime, setLogTime] = useState("");
  const attachRef = useRef(null);
  useEffect(() => { if (open) { setMealSel(meal || "breakfast"); setTab(initialTab || "plan"); setAttach({ path: null, preview: null, busy: false }); setLogTime(nowHHMM()); } }, [open, meal, initialTab]);

  const [usuals, setUsuals] = useState({});
  const [favourites, setFavourites] = useState([]);
  const [scans, setScans] = useState([]);
  const [customPrefill, setCustomPrefill] = useState(null); // {name, unit, code} — set by BarcodeTab when OFF has no macros
  useEffect(() => { if (!open) setCustomPrefill(null); }, [open]);
  useEffect(() => {
    if (!open) return;
    api.get("/tracker/usuals").then(({ data }) => {
      const m = {}; (data.usuals || []).forEach((u) => { m[u.key] = u; }); setUsuals(m);
    }).catch(() => {});
    // Favourites live in their own store (also powers the Tracker quick-log
    // strip). The Favourites tab used to only read "usuals", so anything
    // starred on the Tracker looked like it had vanished (bug report June 2026).
    api.get("/tracker/quicklog").then(({ data }) => {
      setFavourites(data.favourites || []);
    }).catch(() => {});
    api.get("/tracker/scans").then(({ data }) => {
      setScans(data.scans || []);
    }).catch(() => {});
  }, [open]);
  // Silently remember scanned foods (barcode + photo-AI) so athletes can find
  // them later in Search without needing to log them straight away.
  const rememberScan = async (food, source) => {
    if (!food || !food.name) return;
    try {
      const { data } = await api.post("/tracker/scans", {
        name: food.name, source, code: food.code || null, brand: food.brand || null,
        kcal: food.kcal, protein: food.protein, carb: food.carb, fat: food.fat,
        unit: food.unit || null, perServing: food.perServing || null,
        photo_path: food.photo_path || null,
      });
      setScans((prev) => {
        const filtered = prev.filter((s) => s.id !== data.id
          && !(s.code && data.code && s.code === data.code)
          && !(!data.code && s.name === data.name && s.source === data.source));
        return [data, ...filtered].slice(0, 30);
      });
    } catch { /* silent — recent-scans is a nice-to-have */ }
  };
  const removeScan = async (s) => {
    try {
      await api.delete(`/tracker/scans/${s.id}`);
      setScans((prev) => prev.filter((x) => x.id !== s.id));
    } catch { toast.error("Could not remove scan"); }
  };
  const saveUsual = async (food, portion) => {
    try {
      const { data } = await api.post("/tracker/usuals", {
        key: foodKey(food), name: food.name, code: food.code || null,
        qty: portion.qty, unit: portion.unit, grams: portion.grams,
        kcal: portion.kcal, protein: portion.protein, carb: portion.carb, fat: portion.fat,
      });
      setUsuals((m) => ({ ...m, [data.key]: data }));
      // Saving a food also puts it in Favourites so it shows up in this
      // dialog's Favourites tab AND the Tracker quick-log strip.
      try {
        const { data: fav } = await api.post("/tracker/favourites", {
          name: food.name, meal: mealSel, qty: portion.qty, unit: portion.unit,
          kcal: portion.kcal, protein: portion.protein, carb: portion.carb, fat: portion.fat,
          source: food.source || "custom",
        });
        setFavourites((prev) => [fav, ...prev.filter((f) => (f.name || "").toLowerCase() !== (fav.name || "").toLowerCase())]);
      } catch { /* favourites is additive — never block saving the portion */ }
      toast.success("Saved to favourites");
    } catch { toast.error("Could not save your usual"); }
  };
  const removeUsual = async (u) => {
    try {
      await api.delete(`/tracker/usuals/${u.id}`);
      setUsuals((m) => { const c = { ...m }; delete c[u.key]; return c; });
      const fav = favourites.find((f) => (f.name || "").toLowerCase() === (u.name || "").toLowerCase());
      if (fav) {
        try {
          await api.delete(`/tracker/favourites/${fav.id}`);
          setFavourites((prev) => prev.filter((f) => f.id !== fav.id));
        } catch { /* leave the favourite if it's already gone */ }
      }
      toast.success("Removed from favourites");
    } catch { toast.error("Could not remove"); }
  };
  const removeFavourite = async (f) => {
    try {
      await api.delete(`/tracker/favourites/${f.id}`);
      setFavourites((prev) => prev.filter((x) => x.id !== f.id));
      // A bookmarked food lives in BOTH stores — drop the saved portion too,
      // otherwise the row reappears in the merged list.
      const u = Object.values(usuals).find((x) => (x.name || "").toLowerCase() === (f.name || "").toLowerCase());
      if (u) {
        try {
          await api.delete(`/tracker/usuals/${u.id}`);
          setUsuals((m) => { const c = { ...m }; delete c[u.key]; return c; });
        } catch { /* already gone */ }
      }
      toast.success("Removed from favourites");
    } catch { toast.error("Could not remove"); }
  };
  // Legacy rows were keyed differently, so fall back to a name match.
  const usualFor = (food) => usuals[foodKey(food)]
    || Object.values(usuals).find((u) => (u.name || "").toLowerCase() === (food?.name || "").toLowerCase());

  const attachPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAttach((a) => ({ ...a, busy: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/tracker/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAttach({ path: data.photo_path, preview: URL.createObjectURL(file), busy: false });
      toast.success("Photo attached");
    } catch {
      toast.error("Photo upload failed");
      setAttach((a) => ({ ...a, busy: false }));
    }
  };

  const submit = async (entry) => {
    setSaving(true);
    try {
      await api.post("/tracker/log", { date, meal: mealSel, time: logTime, ...entry, photo_path: entry.photo_path || attach.path || null });
      toast.success(`Added ${entry.name}`);
      onAdded && onAdded();
      onOpenChange(false);
    } catch {
      toast.error("Could not add food — please try again");
    } finally { setSaving(false); }
  };

  // Log several AI-parsed items in one go (Describe tab).
  const submitMany = async (items) => {
    setSaving(true);
    try {
      for (const it of items) {
        await api.post("/tracker/log", { date, meal: mealSel, time: logTime, ...it });
      }
      toast.success(`Added ${items.length} item${items.length === 1 ? "" : "s"}`);
      onAdded && onAdded();
      onOpenChange(false);
    } catch {
      toast.error("Could not add those items — please try again");
    } finally { setSaving(false); }
  };

  // Apply a saved meal as individual editable ingredient rows so athletes can
  // tweak, remove or add ingredients on the day without altering the master
  // template (Ann's beta feedback, Feb 2026).
  const submitRecipe = async (recipe) => {
    if (!recipe?.items?.length) return;
    setSaving(true);
    try {
      const { data } = await api.post("/tracker/log/batch", {
        date, meal: mealSel, time: logTime,
        recipe_name: recipe.name,
        recipe_id: recipe.id,
        items: recipe.items,
        source: recipe.source || "recipe",
      });
      toast.success(`Added ${data.count} items from "${recipe.name}" — edit any row individually`);
      onAdded && onAdded();
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail
        || e?.response?.data?.message
        || (typeof e?.response?.data === "string" ? e.response.data : null)
        || e?.message
        || "Could not add meal — please try again";
      toast.error(String(detail).slice(0, 220));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/15 text-white max-w-lg" data-testid="food-log-dialog">
        <DialogHeader><DialogTitle className="font-display uppercase tracking-wide">Add food</DialogTitle></DialogHeader>

        <div className="flex items-center gap-2 flex-wrap justify-between">
          <div className="flex items-center gap-2">
            <span className="text-[11px] uppercase tracking-wider text-zinc-400">Meal</span>
            <div className="flex gap-1 flex-wrap">
              {[...MEALS, ...(MEALS.includes(mealSel) ? [] : [mealSel])].map((m) => (
                <button key={m} data-testid={`meal-pick-${m}`} onClick={() => setMealSel(m)}
                  className={`px-2.5 py-1 text-[11px] uppercase tracking-wide border transition-colors ${mealSel === m ? "border-[#E2E30A] text-[#E2E30A]" : "border-white/15 text-zinc-300 hover:text-white"}`}>{m}</button>
              ))}
            </div>
          </div>
          <input ref={attachRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={attachPhoto} data-testid="attach-photo-input" />
          <div className="flex items-center gap-2">
            {/* Auto-stamped local time — edit if logging after the fact. */}
            <span className="text-[11px] uppercase tracking-wider text-zinc-400">Time</span>
            <TimeField value={logTime} onChange={setLogTime} testid="log-time-input" />
          </div>
          {attach.preview ? (
            <div className="relative">
              <img src={attach.preview} className="h-9 w-9 object-cover rounded" alt="attached food" />
              <button data-testid="attach-photo-remove" onClick={() => setAttach({ path: null, preview: null, busy: false })}
                className="absolute -top-1.5 -right-1.5 bg-black border border-white/20 text-white rounded-full p-0.5"><X className="h-3 w-3" /></button>
            </div>
          ) : (
            <button data-testid="attach-photo-btn" onClick={() => attachRef.current?.click()} disabled={attach.busy}
              className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wide text-zinc-300 border border-white/15 px-2 py-1 hover:text-[#E2E30A] hover:border-[#E2E30A]/50 transition-colors disabled:opacity-50">
              {attach.busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />} Photo
            </button>
          )}
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5 mt-1" data-testid="food-tabs">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`food-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`flex flex-col items-center justify-center gap-1 px-2 py-2 text-[10px] font-semibold uppercase tracking-wide border transition-colors ${tab === t.id ? "border-[#E2E30A] text-[#E2E30A] bg-[#E2E30A]/10" : "border-white/10 text-zinc-400 hover:text-white hover:border-white/25"}`}>
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>

        <div className="min-h-[240px] max-h-[55vh] overflow-y-auto pt-2">
          {tab === "plan" && <PlanTab planMeals={planMeals} onPick={submit} onPickRecipe={submitRecipe} onEditPlanMeal={onEditPlanMeal ? (m) => { onOpenChange(false); onEditPlanMeal(m); } : null} saving={saving} />}
          {tab === "favourites" && <FavouritesTab usuals={usuals} favourites={favourites} onPick={submit} onRemove={removeUsual} onRemoveFav={removeFavourite} saving={saving} />}
          {tab === "describe" && <DescribeTab onLogAll={submitMany} saving={saving} />}
          {tab === "recent" && <RecentsTab scans={scans} onPick={submit} onRemove={removeScan} saving={saving} />}
          {tab === "meals" && <MealsTab onPick={submit} onLogRecipe={submitRecipe} saving={saving} initialBuildItems={initialBuildItems} />}
          {tab === "search" && <SearchTab onPick={submit} saving={saving} usualFor={usualFor} onSaveUsual={saveUsual} onRemoveUsual={removeUsual} scans={scans} onRemoveScan={removeScan} />}
          {tab === "scans" && <ScansTab scans={scans} onPick={submit} saving={saving} usualFor={usualFor} onSaveUsual={saveUsual} onRemoveUsual={removeUsual} onRemoveScan={removeScan} />}
          {tab === "barcode" && <BarcodeTab onPick={submit} saving={saving} usualFor={usualFor} onSaveUsual={saveUsual} onRemoveUsual={removeUsual} onScan={(food) => rememberScan(food, "barcode")} onManualFallback={(pref) => { setCustomPrefill(pref); setTab("custom"); }} />}
          {tab === "custom" && <CustomTab onPick={submit} saving={saving} initial={customPrefill} />}
          {tab === "photo" && <PhotoTab onPick={submit} saving={saving} onScan={(food) => rememberScan(food, "ai")} />}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function planMealToIngredientItems(m) {
  // Prefer explicit per-item macros; else fall back to ingredient names
  // with proportional macro split so athletes always see per-ingredient
  // rows they can edit / swap / re-weigh.
  if (Array.isArray(m?.items) && m.items.length) {
    const anyMacros = m.items.some((it) =>
      (it.kcal || 0) > 0 || (it.protein || 0) > 0 || (it.carb || 0) > 0 || (it.fat || 0) > 0);
    if (anyMacros) {
      return m.items.map((it) => ({
        name: (it.name || m.name || "Ingredient").trim(),
        qty: Number(it.qty || 1),
        unit: it.unit || "serving",
        kcal: Math.round(it.kcal || 0),
        protein: Math.round((it.protein || 0) * 10) / 10,
        carb: Math.round((it.carb || 0) * 10) / 10,
        fat: Math.round((it.fat || 0) * 10) / 10,
      }));
    }
  }
  const ings = Array.isArray(m?.ingredients) ? m.ingredients : (Array.isArray(m?.items) ? m.items : null);
  if (!ings || !ings.length) return null;
  const grams = (ing) => {
    const q = Number(ing?.qty || 0); if (!q) return 0;
    const u = String(ing?.unit || "").toLowerCase().trim();
    if (u === "g" || u === "ml") return q;
    if (u === "kg" || u === "l") return q * 1000;
    if (u === "pcs" || u === "pc" || u === "piece" || u === "pieces") return q * 100;
    if (u === "tbsp") return q * 15;
    if (u === "tsp") return q * 5;
    if (u === "cup") return q * 240;
    if (u === "oz") return q * 28;
    if (u === "slice" || u === "slices") return q * 30;
    if (u === "scoop" || u === "scoops") return q * 30;
    return q * 100;
  };
  const masses = ings.map(grams);
  const total = masses.reduce((a, b) => a + b, 0);
  const even = 1 / ings.length;
  return ings.map((ing, i) => {
    const share = total > 0 ? (masses[i] / total) : even;
    return {
      name: (ing.name || "Ingredient").trim(),
      qty: Number(ing.qty || 1),
      unit: ing.unit || "serving",
      kcal: Math.round((m.kcal || 0) * share),
      protein: Math.round(((m.protein || 0) * share) * 10) / 10,
      carb: Math.round(((m.carb || 0) * share) * 10) / 10,
      fat: Math.round(((m.fat || 0) * share) * 10) / 10,
    };
  });
}

function PlanTab({ planMeals, onPick, onPickRecipe, onEditPlanMeal, saving }) {
  if (!planMeals.length) return <p className="text-sm text-zinc-400 py-6 text-center">No plan meals for this day. Build/generate a plan first, or use another tab.</p>;
  return (
    <div className="space-y-2">
      <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 pb-1">
        Tap a meal to edit ingredients, swap or change qty before logging.
      </p>
      {planMeals.map((m, i) => {
        const items = planMealToIngredientItems(m);
        const asIngredients = !!items;
        return (
          <button key={`${m.slot}-${i}`} data-testid={`plan-meal-${i}`} disabled={saving}
            onClick={() => {
              if (asIngredients && onEditPlanMeal) {
                onEditPlanMeal(m); // opens PathwayMealEditDialog with all ingredients
              } else if (asIngredients) {
                onPickRecipe({ name: m.name, items, source: "pathway" });
              } else {
                onPick({ name: m.name, kcal: m.kcal, protein: m.protein, carb: m.carb, fat: m.fat, source: "plan", qty: 1, unit: "serving" });
              }
            }}
            className="w-full text-left border border-white/10 hover:border-[#E2E30A] p-3 transition-colors disabled:opacity-50">
            <p className="text-[10px] uppercase text-zinc-400">{m.slot}</p>
            <p className="text-sm text-white">{m.name}</p>
            <p className="text-[10px] font-mono text-zinc-300">{m.kcal}kcal · {m.protein}P {m.carb}C {m.fat}F</p>
            {asIngredients && (
              <p className="text-[9px] font-mono text-[#E2E30A] mt-1 uppercase tracking-wider">
                Tap → edit {items.length} ingredients before logging
              </p>
            )}
          </button>
        );
      })}
    </div>
  );
}

// Describe tab — type what you ate in plain English, AI turns it into
// individual items with macros you can tweak before logging.
function DescribeTab({ onLogAll, saving }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState(null);
  const [confidence, setConfidence] = useState(null);

  const parse = async () => {
    if (text.trim().length < 3 || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/ai/parse-food", { text: text.trim() });
      setItems(data.items);
      setConfidence(data.confidence);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not read that");
    } finally { setBusy(false); }
  };

  const setField = (i, field, value) => setItems((prev) =>
    prev.map((it, idx) => (idx === i ? { ...it, [field]: field === "name" ? value : (parseFloat(value) || 0) } : it)));

  const totals = (items || []).reduce((a, i) => ({
    kcal: a.kcal + (i.kcal || 0), protein: a.protein + (i.protein || 0),
    carb: a.carb + (i.carb || 0), fat: a.fat + (i.fat || 0),
  }), { kcal: 0, protein: 0, carb: 0, fat: 0 });

  return (
    <div className="space-y-3" data-testid="describe-tab">
      <p className="text-[11px] text-zinc-300">Type what you ate in your own words — "two slices of toast with peanut butter and a banana" — and we'll break it into items with macros.</p>
      <textarea data-testid="describe-input" rows={3} value={text} onChange={(e) => setText(e.target.value)}
        placeholder="e.g. chicken curry with rice, a naan and a pint of lager"
        className="w-full resize-none bg-black border border-white/15 focus:border-[#A78BFA] outline-none px-3 py-2.5 text-sm text-white placeholder:text-zinc-500" />
      <Button data-testid="describe-parse-btn" onClick={parse} disabled={busy || text.trim().length < 3}
        className="w-full rounded-none bg-[#A78BFA] hover:bg-[#8f6ff5] text-black text-xs uppercase font-semibold">
        {busy ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Working it out…</> : <><Sparkles className="h-4 w-4 mr-2" /> Break it down</>}
      </Button>

      {items && items.length > 0 && (
        <div className="border border-white/10 divide-y divide-white/5" data-testid="describe-items">
          {items.map((it, i) => (
            <div key={i} className="p-3 space-y-2" data-testid={`describe-item-${i}`}>
              <div className="flex items-center gap-2">
                <input value={it.name} onChange={(e) => setField(i, "name", e.target.value)}
                  className="flex-1 min-w-0 bg-transparent text-sm text-white border-b border-white/10 focus:border-[#A78BFA] outline-none pb-1" />
                <button data-testid={`describe-remove-${i}`} onClick={() => setItems(items.filter((_, x) => x !== i))}
                  className="p-1 text-zinc-500 hover:text-[#FF3B30]"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
              <div className="grid grid-cols-5 gap-1.5">
                {[["qty", it.unit], ["kcal", "kcal"], ["protein", "P g"], ["carb", "C g"], ["fat", "F g"]].map(([f, label]) => (
                  <label key={f} className="block">
                    <span className="block text-[9px] uppercase tracking-wider text-zinc-500 mb-0.5">{label}</span>
                    <input data-testid={`describe-${f}-${i}`} type="number" inputMode="decimal" value={it[f]}
                      onChange={(e) => setField(i, f, e.target.value)}
                      className="w-full bg-black border border-white/10 focus:border-[#A78BFA] outline-none px-1.5 py-1 text-xs font-mono text-white" />
                  </label>
                ))}
              </div>
            </div>
          ))}
          <div className="p-3 bg-[#0A0A0A]">
            <p className="text-[11px] font-mono text-zinc-300" data-testid="describe-totals">
              Total {Math.round(totals.kcal)} kcal · {Math.round(totals.protein)}P {Math.round(totals.carb)}C {Math.round(totals.fat)}F
              {confidence && <span className="text-zinc-500"> · {confidence} confidence</span>}
            </p>
            <Button data-testid="describe-log-btn" onClick={() => onLogAll(items)} disabled={saving || !items.length}
              className="w-full mt-2 rounded-none bg-[#E2E30A] hover:bg-[#c9ca09] text-black text-xs uppercase font-semibold">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : `Log ${items.length} item${items.length === 1 ? "" : "s"}`}
            </Button>
            <p className="text-[10px] text-zinc-500 mt-1.5">AI estimate — tweak anything above before logging.</p>
          </div>
        </div>
      )}
      {items && items.length === 0 && (
        <p className="text-sm text-zinc-400">Nothing recognised — try naming the foods and amounts.</p>
      )}
    </div>
  );
}

// Favourites tab — one-tap re-log. Merges the favourites store (also used by
// the Tracker quick-log strip) with saved "usual" portions, deduped by name,
// so anything starred anywhere in the app shows up here.
function FavouritesTab({ usuals, favourites = [], onPick, onRemove, onRemoveFav, saving }) {
  const seen = new Set();
  const list = [];
  (favourites || []).forEach((f) => {
    const sig = (f.name || "").toLowerCase();
    if (!sig || seen.has(sig)) return;
    seen.add(sig);
    list.push({ ...f, kind: "fav" });
  });
  Object.values(usuals || {}).forEach((u) => {
    const sig = (u.name || "").toLowerCase();
    if (!sig || seen.has(sig)) return;
    seen.add(sig);
    list.push({ ...u, kind: "usual" });
  });
  if (!list.length) return (
    <p className="text-sm text-zinc-400 py-6 text-center">No favourites yet. Tap the bookmark on any Search, Barcode or Photo AI result — or the star on the Tracker's quick-log strip — to save it here.</p>
  );
  return (
    <div className="space-y-2" data-testid="favourites-tab">
      <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 pb-1">
        Tap a favourite to log it under {" "} the selected meal.
      </p>
      {list.map((u) => (
        <div key={u.id || u.key} className="flex items-center gap-2 border border-white/10 hover:border-[#E2E30A]/50 transition-colors">
          <button data-testid={`favourite-pick-${(u.name || "").toLowerCase().replace(/\W+/g, "-")}`}
            disabled={saving}
            onClick={() => onPick({
              name: u.name, kcal: u.kcal, protein: u.protein, carb: u.carb, fat: u.fat,
              qty: u.qty || 1, unit: u.unit || "serving", grams: u.grams || null,
              code: u.code || null, source: "usual",
            })}
            className="flex-1 min-w-0 text-left p-3 disabled:opacity-50">
            <p className="text-sm text-white truncate flex items-center gap-2">
              <Star className="h-3.5 w-3.5 fill-[#E2E30A] text-[#E2E30A] shrink-0" />
              {u.name}
            </p>
            <p className="text-[10px] font-mono text-zinc-300 mt-0.5">
              {Math.round(u.kcal || 0)}kcal · {Math.round(u.protein || 0)}P {Math.round(u.carb || 0)}C {Math.round(u.fat || 0)}F
              {u.qty && u.unit ? ` · ${u.qty}${u.unit === "item" ? "×" : u.unit}` : ""}
            </p>
          </button>
          <button data-testid={`favourite-remove-${(u.name || "").toLowerCase().replace(/\W+/g, "-")}`}
            onClick={() => (u.kind === "fav" ? onRemoveFav(u) : onRemove(u))} title="Remove favourite"
            className="px-3 self-stretch flex items-center border-l border-white/10 text-zinc-400 hover:text-[#FF3B30] transition-colors">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
}

// Recent tab — last 25 barcode/photo-AI scanned foods, re-log with a tap.
function RecentsTab({ scans, onPick, onRemove, saving }) {
  const list = (scans || []).slice(0, 25);
  if (!list.length) return (
    <p className="text-sm text-zinc-400 py-6 text-center">No recent foods yet. Anything you scan via Barcode or Photo AI shows up here for one-tap re-logging.</p>
  );
  return (
    <div className="space-y-2" data-testid="recent-tab">
      <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 pb-1">
        Last {list.length} · tap to re-log under the selected meal.
      </p>
      {list.map((s) => (
        <div key={s.id} className="flex items-center gap-2 border border-white/10 hover:border-[#4C9AFF]/50 transition-colors">
          <button data-testid={`recent-pick-${(s.name || "").toLowerCase().replace(/\W+/g, "-")}`}
            disabled={saving}
            onClick={() => onPick({
              name: s.name, kcal: s.kcal, protein: s.protein, carb: s.carb, fat: s.fat,
              qty: 1, unit: s.unit || "serving",
              code: s.code || null, source: s.source || "scan",
              photo_path: s.photo_path || null,
            })}
            className="flex-1 min-w-0 text-left p-3 disabled:opacity-50">
            <p className="text-sm text-white truncate flex items-center gap-2">
              <Zap className="h-3.5 w-3.5 text-[#4C9AFF] shrink-0" />
              {s.name}
              {s.brand && <span className="text-[10px] text-zinc-400">· {s.brand}</span>}
            </p>
            <p className="text-[10px] font-mono text-zinc-300 mt-0.5">
              {Math.round(s.kcal || 0)}kcal · {Math.round(s.protein || 0)}P {Math.round(s.carb || 0)}C {Math.round(s.fat || 0)}F
              <span className="text-zinc-500"> · {s.source === "ai" ? "Photo AI" : "Barcode"}</span>
            </p>
          </button>
          <button data-testid={`recent-remove-${(s.name || "").toLowerCase().replace(/\W+/g, "-")}`}
            onClick={() => onRemove(s)} title="Remove from recent"
            className="px-3 self-stretch flex items-center border-l border-white/10 text-zinc-400 hover:text-[#FF3B30] transition-colors">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
}

const SOLID_PORTIONS = [
  { label: "1 tbsp", g: 15 },
  { label: "1 slice", g: 30 },
  { label: "1 handful", g: 30 },
  { label: "Small", g: 80 },
  { label: "Medium", g: 120 },
  { label: "Large", g: 200 },
  { label: "100g", g: 100 },
];
const LIQUID_PORTIONS = [
  { label: "1 shot", g: 30 },
  { label: "Small glass", g: 150 },
  { label: "Glass", g: 250 },
  { label: "Can", g: 330 },
  { label: "Bottle", g: 500 },
  { label: "100ml", g: 100 },
];

function PortionAdd({ food, onPick, saving, ctaLabel = "Add to log", initialGrams, usual, onSaveUsual }) {
  const unit = food.unit === "ml" ? "ml" : "g";
  const presets = unit === "ml" ? LIQUID_PORTIONS : SOLID_PORTIONS;

  // Per-serving nutrition = one drink / one item as sold or prepared. Prefer
  // Open Food Facts' own per-serving figures; otherwise derive one serving from
  // the per-100 values and the serving size. This is what the athlete actually
  // eats, so it's the default — no more logging a whole 16-capsule pack.
  const ps = food.perServing && food.perServing.kcal > 0 ? food.perServing : null;
  const servMacros = ps || (food.serving ? {
    label: food.servingLabel || `${food.serving}${unit}`,
    grams: food.serving,
    kcal: scale(food.kcal, food.serving),
    protein: scale(food.protein, food.serving),
    carb: scale(food.carb, food.serving),
    fat: scale(food.fat, food.serving),
  } : null);
  const servGrams = (servMacros && servMacros.grams) || null;
  const hasServing = !!servMacros;

  const [mode, setMode] = useState(usual ? (usual.unit === "serving" ? "serving" : "weight") : (hasServing ? "serving" : "weight"));
  const [grams, setGrams] = useState(usual && usual.unit !== "serving" ? (usual.grams || usual.qty || 100) : (initialGrams || food.serving || 100));
  const [count, setCount] = useState(
    usual && usual.unit === "serving" ? (usual.qty || 1)
      : (hasServing && initialGrams && servGrams ? Math.max(1, Math.round(initialGrams / servGrams)) : 1)
  );

  const c = Number(count) || 0;
  const g = mode === "weight" ? (Number(grams) || 0) : (servGrams ? servGrams * c : 0);
  const servingKcal = servMacros ? Math.round(servMacros.kcal * c) : 0;
  const shownKcal = mode === "serving" ? servingKcal : scale(food.kcal, g);
  const servLabel = servMacros ? servMacros.label : "";
  const hasPer100 = (food.kcal || 0) > 0;

  const buildPortion = () => {
    if (mode === "serving") {
      return {
        name: c > 1 ? `${food.name} ×${c}` : food.name,
        source: food.source || "database",
        qty: c, unit: "serving",
        grams: servGrams ? servGrams * c : c,
        kcal: Math.round(servMacros.kcal * c),
        protein: r1(servMacros.protein * c),
        carb: r1(servMacros.carb * c),
        fat: r1(servMacros.fat * c),
      };
    }
    return {
      name: food.name,
      source: food.source || "database",
      qty: Number(grams) || 0, unit,
      grams: Number(grams) || 0,
      kcal: scale(food.kcal, Number(grams) || 0), protein: scale(food.protein, Number(grams) || 0),
      carb: scale(food.carb, Number(grams) || 0), fat: scale(food.fat, Number(grams) || 0),
    };
  };
  const confirm = () => onPick(buildPortion());
  const usualPortion = usual ? {
    name: usual.name, source: food.source || "database",
    qty: usual.qty, unit: usual.unit, grams: usual.grams,
    kcal: usual.kcal, protein: usual.protein, carb: usual.carb, fat: usual.fat,
  } : null;

  const modeBtn = (m, label, testid) => (
    <button type="button" data-testid={testid} onClick={() => setMode(m)}
      className={`px-2.5 py-1 text-[11px] border uppercase tracking-wide transition-colors ${mode === m ? "border-[#E2E30A] text-[#E2E30A]" : "border-white/15 text-zinc-300 hover:text-white"}`}>{label}</button>
  );

  return (
    <div className="border border-[#E2E30A]/40 p-3 mt-2 bg-[#E2E30A]/[0.05]">
      <p className="text-sm text-white">{food.name}{food.brand ? ` · ${food.brand}` : ""}</p>
      <p className="text-[10px] font-mono text-zinc-300 mb-2">
        {servMacros
          ? `Per serving (${servLabel}): ${Math.round(servMacros.kcal)}kcal · ${r1(servMacros.protein)}P ${r1(servMacros.carb)}C ${r1(servMacros.fat)}F`
          : `Per 100${unit}: ${food.kcal}kcal · ${food.protein}P ${food.carb}C ${food.fat}F`}
      </p>
      {hasServing && (
        <div className="flex gap-1 mb-2">
          {modeBtn("serving", "Per serving", "portion-mode-serving")}
          {hasPer100 && modeBtn("weight", "Weight", "portion-mode-weight")}
        </div>
      )}
      {mode === "serving" ? (
        <div className="flex items-center gap-2">
          <span className="text-xs text-zinc-300">Servings</span>
          <button type="button" data-testid="item-minus" onClick={() => setCount((v) => Math.max(1, (Number(v) || 1) - 1))}
            className="w-8 h-8 border border-white/15 text-white hover:border-[#E2E30A]">−</button>
          <Input data-testid="portion-items" type="number" min="1" step="1" value={count}
            onChange={(e) => setCount(e.target.value)} className="w-16 bg-black border-white/15 text-white text-center" />
          <button type="button" data-testid="item-plus" onClick={() => setCount((v) => (Number(v) || 1) + 1)}
            className="w-8 h-8 border border-white/15 text-white hover:border-[#E2E30A]">+</button>
          <span className="text-xs text-zinc-400">× {servLabel}</span>
          <span className="ml-auto text-xs font-mono text-[#E2E30A]">{servingKcal}kcal</span>
        </div>
      ) : (
        <>
          <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1">Common portions</p>
          <div className="flex flex-wrap gap-1 mb-2">
            {presets.map((p) => (
              <button key={p.label} type="button" data-testid={`portion-${p.g}`} onClick={() => setGrams(p.g)}
                className={`px-2 py-1 text-[11px] border transition-colors ${Number(grams) === p.g ? "border-[#E2E30A] text-[#E2E30A]" : "border-white/15 text-zinc-300 hover:text-white hover:border-white/40"}`}>
                {p.label} <span className="text-zinc-300">{p.g}{unit}</span>
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Input data-testid="portion-grams" type="number" min="1" value={grams}
              onChange={(e) => setGrams(e.target.value)} className="w-24 bg-black border-white/15 text-white" />
            <span className="text-xs text-zinc-400">{unit}</span>
            <span className="ml-auto text-xs font-mono text-[#E2E30A]">{scale(food.kcal, Number(grams) || 0)}kcal</span>
          </div>
        </>
      )}
      {usual && usualPortion && (
        <div className="flex items-center justify-between gap-2 mt-2 border border-[#4C9AFF]/40 bg-[#4C9AFF]/[0.06] px-2 py-1.5" data-testid="usual-banner">
          <span className="text-[11px] text-[#4C9AFF]">My usual · {usual.unit === "serving" ? `×${usual.qty}` : `${usual.grams || usual.qty}${usual.unit || "g"}`} · {Math.round(usual.kcal)}kcal</span>
          <button type="button" data-testid="log-usual-btn" disabled={saving} onClick={() => onPick(usualPortion)}
            className="shrink-0 text-[11px] uppercase tracking-wide bg-[#4C9AFF] text-black px-2.5 py-1 font-semibold hover:bg-[#3a86e6] transition-colors disabled:opacity-50">Log usual</button>
        </div>
      )}
      <div className="flex gap-2 mt-2">
        <Button data-testid="confirm-portion-add" disabled={saving || shownKcal <= 0} onClick={confirm}
          className="flex-1 rounded-none bg-[#E2E30A] hover:bg-[#c9ca09] text-black text-xs uppercase font-semibold">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : ctaLabel}
        </Button>
        {onSaveUsual && (
          <button type="button" data-testid={usual ? "update-usual-btn" : "save-usual-btn"} disabled={shownKcal <= 0}
            onClick={() => onSaveUsual(food, buildPortion())} title={usual ? "Update this favourite's portion" : "Save to favourites"}
            className={`shrink-0 px-3 border transition-colors disabled:opacity-40 ${usual ? "border-[#4C9AFF]/60 text-[#4C9AFF] bg-[#4C9AFF]/10" : "border-white/15 text-zinc-300 hover:text-[#4C9AFF] hover:border-[#4C9AFF]/50"}`}>
            <Bookmark className={`h-4 w-4 ${usual ? "fill-current" : ""}`} />
          </button>
        )}
      </div>
    </div>
  );
}

function MealsTab({ onPick, onLogRecipe, saving, initialBuildItems }) {
  const [view, setView] = useState(initialBuildItems?.length ? "build" : "list");
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [items, setItems] = useState(() => (initialBuildItems || []).map((it, i) => ({
    key: `seed-${i}-${it.name}`, food: null,
    grams: it.grams || null,
    name: it.name, qty: it.qty ?? 1, unit: it.unit || "serving",
    kcal: it.kcal || 0, protein: it.protein || 0, carb: it.carb || 0, fat: it.fat || 0,
  })));
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const [searching, setSearching] = useState(false);
  const [pickCode, setPickCode] = useState(null);
  const [editKey, setEditKey] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/tracker/recipes"); setRecipes(data.recipes || []); }
    catch { /* noop */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const totals = items.reduce((a, i) => ({ kcal: a.kcal + i.kcal, protein: a.protein + i.protein, carb: a.carb + i.carb, fat: a.fat + i.fat }), { kcal: 0, protein: 0, carb: 0, fat: 0 });
  const doSearch = async () => {
    if (q.trim().length < 2) return;
    setSearching(true); setPickCode(null);
    try { const { data } = await api.get(`/tracker/search?q=${encodeURIComponent(q.trim())}`); setRes(data.results || []); }
    catch { toast.error("Search busy — try again in a moment"); } finally { setSearching(false); }
  };
  const addFromPortion = (food, p) => {
    setItems((x) => [...x, { key: `${Date.now()}-${Math.random()}`, food, grams: p.grams, name: p.name, qty: p.qty, unit: p.unit, kcal: p.kcal, protein: p.protein, carb: p.carb, fat: p.fat }]);
    setPickCode(null); setRes([]); setQ("");
  };
  const updateItem = (key, p) => {
    setItems((x) => x.map((it) => it.key === key ? { ...it, grams: p.grams, name: p.name, qty: p.qty, unit: p.unit, kcal: p.kcal, protein: p.protein, carb: p.carb, fat: p.fat } : it));
    setEditKey(null);
  };
  const save = async () => {
    if (!name.trim() || !items.length) { toast.error("Add a name and at least one food"); return; }
    const payload = items.map((it) => ({ name: it.name, qty: it.qty, unit: it.unit, kcal: it.kcal, protein: it.protein, carb: it.carb, fat: it.fat }));
    try { await api.post("/tracker/recipes", { name: name.trim(), items: payload }); toast.success("Meal saved"); setName(""); setItems([]); setView("list"); load(); }
    catch (e) {
      // Surface the backend detail so we know WHY the save failed instead of a
      // generic "Could not save meal" — bug reported on live 16 Aug 2026.
      const detail = e?.response?.data?.detail
        || e?.response?.data?.message
        || (typeof e?.response?.data === "string" ? e.response.data : null)
        || e?.message
        || "Could not save meal";
      // eslint-disable-next-line no-console
      console.error("save recipe failed", e?.response?.status, e?.response?.data || e);
      toast.error(String(detail).slice(0, 220));
    }
  };
  const logRecipe = (r) => onLogRecipe && onLogRecipe(r);
  const del = async (id) => { try { await api.delete(`/tracker/recipes/${id}`); load(); } catch { /* noop */ } };

  if (view === "build") {
    return (
      <div data-testid="recipe-builder">
        <Input data-testid="recipe-name" value={name} onChange={(e) => setName(e.target.value)}
          placeholder="Meal name (e.g. Beef, rice & broccoli)" className="bg-black border-white/15 text-white mb-2" />
        <div className="flex gap-2 mb-2">
          <Input data-testid="recipe-search" value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && doSearch()} placeholder="Search a food to add" className="bg-black border-white/15 text-white" />
          <Button data-testid="recipe-search-btn" onClick={doSearch} className="rounded-none bg-[#E2E30A] hover:bg-[#c9ca09] text-black font-semibold">{searching ? <Loader2 className="h-4 w-4 animate-spin" /> : "Find"}</Button>
        </div>
        {res.length > 0 && (
          <div className="space-y-1 mb-2 max-h-56 overflow-y-auto">
            {res.map((f, ri) => {
              const fc = f.code || f.name;
              return (
                <div key={`${fc}-${ri}`}>
                  <button data-testid={`recipe-add-${fc}`} onClick={() => setPickCode(pickCode === fc ? null : fc)}
                    className={`w-full text-left text-xs border p-2 transition-colors ${pickCode === fc ? "border-[#4C9AFF] text-white" : "border-white/10 hover:border-[#4C9AFF] text-zinc-200"}`}>
                    {f.name}{f.brand ? ` · ${f.brand}` : ""} <span className="text-zinc-300">{f.kcal}kcal/100{f.unit === "ml" ? "ml" : "g"}</span>
                  </button>
                  {pickCode === fc && <PortionAdd food={{ ...f, source: "database" }} onPick={(p) => addFromPortion(f, p)} saving={false} ctaLabel="Add to meal" />}
                </div>
              );
            })}
          </div>
        )}
        <div className="space-y-1 mb-2">
          {items.map((it) => (
            <div key={it.key}>
              <div className="flex items-center justify-between text-xs border-b border-white/5 pb-1">
                <span className="text-white">{it.name} <span className="text-zinc-300">{it.qty}{it.unit === "item" ? "×" : it.unit}</span></span>
                <span className="flex items-center gap-2"><span className="font-mono text-zinc-300">{Math.round(it.kcal)}kcal</span>
                  <button data-testid={`recipe-item-edit-${it.key}`} title="Edit portion" onClick={() => setEditKey(editKey === it.key ? null : it.key)} className="text-zinc-400 hover:text-[#4C9AFF]"><PenLine className="h-3.5 w-3.5" /></button>
                  <button data-testid={`recipe-item-remove-${it.key}`} title="Remove" onClick={() => setItems((x) => x.filter((y) => y.key !== it.key))} className="text-zinc-400 hover:text-[#FF3B30]"><Trash2 className="h-3.5 w-3.5" /></button>
                </span>
              </div>
              {editKey === it.key && <PortionAdd food={{ ...it.food, source: "database" }} initialGrams={it.grams} onPick={(p) => updateItem(it.key, p)} saving={false} ctaLabel="Update portion" />}
            </div>
          ))}
          {!items.length && <p className="text-xs text-zinc-300 py-2">No foods yet — search a food and tap it to set the amount.</p>}
        </div>
        <p className="text-xs font-mono text-[#E2E30A] mb-2">Total: {Math.round(totals.kcal)}kcal · {Math.round(totals.protein)}P {Math.round(totals.carb)}C {Math.round(totals.fat)}F</p>
        <div className="flex gap-2">
          <Button data-testid="recipe-save-btn" onClick={save} className="flex-1 rounded-none bg-[#E2E30A] hover:bg-[#c9ca09] text-black text-xs uppercase font-semibold">Save meal</Button>
          <Button data-testid="recipe-cancel-btn" onClick={() => setView("list")} variant="outline" className="rounded-none border-white/15 bg-transparent text-white text-xs uppercase">Cancel</Button>
        </div>
      </div>
    );
  }
  return (
    <div data-testid="meals-list">
      <Button data-testid="recipe-new-btn" onClick={() => setView("build")} className="w-full mb-3 rounded-none bg-[#E2E30A] hover:bg-[#c9ca09] text-black text-xs uppercase font-semibold"><Plus className="h-4 w-4 mr-1" /> Create a meal</Button>
      {recipes.length > 0 && (
        <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-2">Tapping a meal adds each ingredient as its own editable row · your template stays untouched.</p>
      )}
      {loading ? <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-[#E2E30A]" /></div>
        : recipes.length === 0 ? <p className="text-sm text-zinc-400 py-4 text-center">No saved meals yet. Create one to log it in a single tap.</p>
        : <div className="space-y-2">{recipes.map((r) => (
            <div key={r.id} data-testid={`recipe-${r.id}`} className="border border-white/10 p-3 flex items-center justify-between gap-2">
              <button disabled={saving} onClick={() => logRecipe(r)} data-testid={`recipe-log-${r.id}`} className="flex-1 text-left disabled:opacity-50">
                <p className="text-sm text-white">{r.name}</p>
                <p className="text-[10px] font-mono text-zinc-300">{r.totals.kcal}kcal · {r.totals.protein}P {r.totals.carb}C {r.totals.fat}F · {r.items.length} items</p>
              </button>
              <button data-testid={`recipe-del-${r.id}`} onClick={() => del(r.id)} className="text-zinc-400 hover:text-[#FF3B30] p-1"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}</div>}
    </div>
  );
}

function SearchTab({ onPick, saving, usualFor = () => null, onSaveUsual, onRemoveUsual, scans = [], onRemoveScan }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [sel, setSel] = useState(null);
  const [scanSel, setScanSel] = useState(null);
  const [enriching, setEnriching] = useState(null);
  // Filter the athlete's recent scans by the current search term so they surface
  // near the top of Search — matches on name or brand.
  const filteredScans = (() => {
    const term = q.trim().toLowerCase();
    if (!term) return scans.slice(0, 6);
    return scans.filter((s) =>
      (s.name || "").toLowerCase().includes(term)
      || (s.brand || "").toLowerCase().includes(term)
    ).slice(0, 6);
  })();
  const run = async (term) => {
    const query = (term ?? q).trim();
    if (query.length < 2) return;
    setLoading(true); setSel(null);
    try { const { data } = await api.get(`/tracker/search?q=${encodeURIComponent(query)}`); setResults(data.results || []); setSearched(true); }
    catch (e) { setResults([]); toast.error(e?.response?.data?.detail || "Food search is temporarily unavailable — please try again"); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    const query = q.trim();
    if (query.length < 2) { setResults([]); setSearched(false); return; }
    const id = setTimeout(() => run(query), 350);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);
  // Fetch the full product on selection: the fast search index omits per-serving
  // nutrition, so we look the product up to get its true "1 serving" figures.
  const pick = async (r) => {
    if (sel?.code === r.code) { setSel(null); return; }
    if (!r.code || r.code.startsWith("basic:") || r.source === "basic" || r.source === "usda") { setSel({ ...r }); return; }
    setEnriching(r.code);
    try {
      const { data } = await api.get(`/tracker/barcode/${encodeURIComponent(r.code)}`);
      // Non-destructive merge: never lose the nutrition the search list already
      // had. Prefer the enriched product only where it actually carries data.
      const merged = { ...r, ...data };
      if (!data.kcal && r.kcal) merged.kcal = r.kcal;
      if (!(data.protein || data.carb || data.fat) && (r.protein || r.carb || r.fat)) {
        merged.protein = r.protein; merged.carb = r.carb; merged.fat = r.fat;
      }
      merged.perServing = data.perServing || r.perServing || null;
      setSel({ ...merged, source: "database" });
    } catch {
      setSel({ ...r, source: "database" });
    } finally { setEnriching(null); }
  };
  return (
    <div>
      <div className="flex gap-2">
        <Input data-testid="food-search-input" value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && run()} placeholder="e.g. scrambled egg, banana, chicken" className="bg-black border-white/15 text-white" />
        <Button data-testid="food-search-btn" onClick={() => run()} className="rounded-none bg-[#E2E30A] hover:bg-[#c9ca09] text-black font-semibold">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
        </Button>
      </div>
      {searched && !loading && results.length === 0 && (
        <p data-testid="search-no-results" className="text-xs text-zinc-400 py-4 text-center">No matches found — try a different name or use the Custom tab.</p>
      )}
      {filteredScans.length > 0 && (
        <div className="mt-3 border border-white/10 bg-[#0F0F0F]" data-testid="search-recent-scans">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#E2E30A] font-bold px-3 pt-2.5 pb-1.5">Recent scans</p>
          <div className="space-y-1 px-2 pb-2">
            {filteredScans.map((s) => (
              <div key={s.id}>
                <div className="flex items-center gap-1">
                  <button data-testid={`scan-pick-${s.id}`} type="button"
                    onClick={() => setScanSel((cur) => (cur?.id === s.id ? null : s))}
                    className={`flex-1 text-left border p-2 transition-colors ${scanSel?.id === s.id ? "border-[#E2E30A]" : "border-white/10 hover:border-white/30"}`}>
                    <p className="text-sm text-white flex items-center gap-2">
                      <span className="text-[8px] font-bold uppercase tracking-wider border px-1 py-0.5 text-[#E2E30A] border-[#E2E30A]/40">{s.source === "ai" ? "AI" : "Scan"}</span>
                      {s.name}{s.brand ? ` · ${s.brand}` : ""}
                    </p>
                    <p className="text-[10px] font-mono text-zinc-400">
                      {s.perServing ? `${s.perServing.kcal}kcal · ${s.perServing.label}` : `${s.kcal}kcal${s.protein != null ? ` · P${s.protein} C${s.carb} F${s.fat}` : ""}`}
                    </p>
                  </button>
                  <button type="button" data-testid={`scan-remove-${s.id}`}
                    onClick={() => onRemoveScan?.(s)} title="Remove scan"
                    className="p-2 text-zinc-500 hover:text-[#FF3B30] transition-colors">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
                {scanSel?.id === s.id && (
                  <PortionAdd
                    food={{
                      name: s.name, code: s.code, brand: s.brand,
                      kcal: s.kcal, protein: s.protein, carb: s.carb, fat: s.fat,
                      unit: s.unit, perServing: s.perServing, photo_path: s.photo_path,
                      source: s.source === "ai" ? "ai" : "barcode",
                    }}
                    onPick={onPick} saving={saving}
                    usual={usualFor(s)}
                    onSaveUsual={onSaveUsual} onRemoveUsual={onRemoveUsual}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="mt-2 space-y-1">
        {results.map((r, ri) => (
          <div key={`${r.code || r.name}-${ri}`}>
            <button data-testid={`search-result-${r.code}`} onClick={() => pick(r)}
              className={`w-full text-left border p-2 transition-colors flex items-center justify-between gap-2 ${sel?.code === r.code ? "border-[#E2E30A]" : "border-white/10 hover:border-white/30"}`}>
              <span>
                <p className="text-sm text-white">
                  {r.name}{r.brand ? ` · ${r.brand}` : ""}
                  {r.source === "basic" && <span className={`ml-2 align-middle text-[8px] uppercase tracking-wider border px-1 py-0.5 ${r.tag === "Home" ? "text-[#FF9500] border-[#FF9500]/40" : "text-[#34C759] border-[#34C759]/40"}`}>{r.tag || "Basic"}</span>}
                  {r.source === "usda" && <span className="ml-2 align-middle text-[8px] uppercase tracking-wider border px-1 py-0.5 text-[#4C9AFF] border-[#4C9AFF]/40">USDA</span>}
                </p>
                <p className="text-[10px] font-mono text-zinc-400">{r.perServing ? `${r.perServing.kcal}kcal · ${r.perServing.label}` : `${r.kcal}kcal/100${r.unit === "ml" ? "ml" : "g"}`}</p>
              </span>
              {enriching === r.code && <Loader2 className="h-4 w-4 animate-spin text-[#E2E30A] shrink-0" />}
            </button>
            {sel?.code === r.code && enriching !== r.code && <PortionAdd food={{ ...sel, source: "database" }} onPick={onPick} saving={saving} usual={usualFor(sel)} onSaveUsual={onSaveUsual} onRemoveUsual={onRemoveUsual} />}
          </div>
        ))}
      </div>
    </div>
  );
}

function ScansTab({ scans = [], onPick, saving, usualFor = () => null, onSaveUsual, onRemoveUsual, onRemoveScan }) {
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  // Match on name or brand. Term is lowercased once for a cheap includes-check.
  const list = (() => {
    const term = q.trim().toLowerCase();
    if (!term) return scans;
    return scans.filter((s) =>
      (s.name || "").toLowerCase().includes(term)
      || (s.brand || "").toLowerCase().includes(term)
    );
  })();
  if (!scans.length) {
    return (
      <div className="py-10 text-center border border-white/10 bg-[#0F0F0F]">
        <ScanLine className="h-8 w-8 mx-auto text-zinc-500 mb-2" />
        <p className="text-sm text-zinc-300">No scans yet.</p>
        <p className="text-[11px] text-zinc-500 mt-1">Scan a barcode or take a food photo — it'll appear here even if you don't log it.</p>
      </div>
    );
  }
  return (
    <div>
      <div className="relative mb-2">
        <Search className="h-3.5 w-3.5 text-zinc-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
        <Input data-testid="scans-search-input" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Filter scans…" className="pl-8 bg-black border-white/15 text-white" />
      </div>
      <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-2" data-testid="scans-count">
        {list.length} of {scans.length} scan{scans.length === 1 ? "" : "s"} — newest first
      </p>
      {list.length === 0 && (
        <p data-testid="scans-empty" className="text-xs text-zinc-400 py-4 text-center">No scans match that filter.</p>
      )}
      <div className="space-y-1">
        {list.map((s) => (
          <div key={s.id}>
            <div className="flex items-center gap-1">
              <button data-testid={`scans-tab-pick-${s.id}`} type="button"
                onClick={() => setSel((cur) => (cur?.id === s.id ? null : s))}
                className={`flex-1 text-left border p-2 transition-colors ${sel?.id === s.id ? "border-[#E2E30A]" : "border-white/10 hover:border-white/30"}`}>
                <p className="text-sm text-white flex items-center gap-2">
                  <span className={`text-[8px] font-bold uppercase tracking-wider border px-1 py-0.5 ${s.source === "ai" ? "text-[#FF9500] border-[#FF9500]/40" : "text-[#E2E30A] border-[#E2E30A]/40"}`}>{s.source === "ai" ? "AI" : "Scan"}</span>
                  {s.name}{s.brand ? ` · ${s.brand}` : ""}
                </p>
                <p className="text-[10px] font-mono text-zinc-400">
                  {s.perServing ? `${s.perServing.kcal}kcal · ${s.perServing.label}` : `${s.kcal}kcal${s.protein != null ? ` · P${s.protein} C${s.carb} F${s.fat}` : ""}`}
                  {s.scanned_at && <span className="text-zinc-600"> · {new Date(s.scanned_at).toLocaleDateString()}</span>}
                </p>
              </button>
              <button type="button" data-testid={`scans-tab-remove-${s.id}`}
                onClick={() => onRemoveScan?.(s)} title="Remove scan"
                className="p-2 text-zinc-500 hover:text-[#FF3B30] transition-colors">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            {sel?.id === s.id && (
              <PortionAdd
                food={{
                  name: s.name, code: s.code, brand: s.brand,
                  kcal: s.kcal, protein: s.protein, carb: s.carb, fat: s.fat,
                  unit: s.unit, perServing: s.perServing, photo_path: s.photo_path,
                  source: s.source === "ai" ? "ai" : "barcode",
                }}
                onPick={onPick} saving={saving}
                usual={usualFor(s)}
                onSaveUsual={onSaveUsual} onRemoveUsual={onRemoveUsual}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


function BarcodeTab({ onPick, saving, usualFor = () => null, onSaveUsual, onRemoveUsual, onScan, onManualFallback }) {
  const [code, setCode] = useState("");
  const [food, setFood] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [manual, setManual] = useState(false);
  const [camError, setCamError] = useState("");
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [zoomCaps, setZoomCaps] = useState(null); // {min, max, step}
  const [scanHint, setScanHint] = useState("Point at a barcode…");
  const videoRef = useRef(null);
  const controlsRef = useRef(null);
  const trackRef = useRef(null);
  const lastCodeRef = useRef({ code: "", at: 0 });

  const lookup = async (c) => {
    if (!c) return;
    setLoading(true); setFood(null);
    try {
      const { data } = await api.get(`/tracker/barcode/${encodeURIComponent(c)}`);
      setFood(data);
      // Remember every successful lookup so it's findable later in Search
      // even if the athlete doesn't log it right now.
      onScan?.(data);
    }
    catch (e) { toast.error(e?.response?.data?.detail || "No product found"); }
    finally { setLoading(false); }
  };

  const stopScan = () => {
    try { controlsRef.current && controlsRef.current.stop(); } catch { /* noop */ }
    controlsRef.current = null;
    trackRef.current = null;
    setTorchSupported(false);
    setTorchOn(false);
    setZoomCaps(null);
    setZoom(1);
    setScanning(false);
  };

  // Format restriction speeds up decoding on cheap phones — food packaging is
  // ~always EAN-13 / EAN-8 / UPC-A/E; leaving CODE_128 in for supplement labels.
  const buildHints = () => {
    const hints = new Map();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [
      BarcodeFormat.EAN_13, BarcodeFormat.EAN_8,
      BarcodeFormat.UPC_A, BarcodeFormat.UPC_E,
      BarcodeFormat.CODE_128, BarcodeFormat.CODE_39,
    ]);
    hints.set(DecodeHintType.TRY_HARDER, true);
    return hints;
  };

  // Camera start MUST be triggered by a user gesture (tap) — browsers (esp. iOS
  // Safari and embedded contexts) reject a gesture-less getUserMedia with a
  // permission/"NotAllowed" error, which surfaced as an unauthorised runtime error.
  const startScan = async () => {
    setCamError(""); setFood(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      setCamError("This browser can't open the camera here — enter the barcode number instead.");
      setManual(true);
      return;
    }
    setScanning(true);
    // Wait a frame so the <video> element is mounted & laid out before attaching
    // the camera stream — otherwise ZXing receives a null element and the preview
    // stays black (the Android "black window" bug).
    await new Promise((r) => requestAnimationFrame(() => r()));
    if (!videoRef.current) { setScanning(false); return; }

    // Try HD constraints first (better decode on small labels); fall back to
    // default if the device can't satisfy them. Ask for continuous focus so
    // athletes don't have to tap-to-focus repeatedly.
    const constraintTiers = [
      { video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1920 }, height: { ideal: 1080 },
          focusMode: "continuous",
          advanced: [{ focusMode: "continuous" }],
        } },
      { video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1280 }, height: { ideal: 720 },
        } },
      { video: { facingMode: "environment" } },
      { video: true },
    ];

    let lastErr = null;
    for (const constraints of constraintTiers) {
      try {
        const reader = new BrowserMultiFormatReader(buildHints());
        controlsRef.current = await reader.decodeFromConstraints(
          constraints, videoRef.current,
          (result) => {
            if (!result) return;
            const c = result.getText();
            const now = Date.now();
            // Ignore a duplicate hit within 1.5s — ZXing sometimes fires the
            // same result twice while the frame is still in view.
            if (lastCodeRef.current.code === c && now - lastCodeRef.current.at < 1500) return;
            lastCodeRef.current = { code: c, at: now };
            setScanHint("Got it — looking up…");
            try { navigator.vibrate?.(50); } catch { /* noop */ }
            setCode(c);
            stopScan();
            lookup(c);
          }
        );
        // Grab the active track so we can expose torch + zoom controls.
        const stream = videoRef.current.srcObject;
        const track = stream && stream.getVideoTracks && stream.getVideoTracks()[0];
        trackRef.current = track || null;
        try {
          const caps = track?.getCapabilities?.() || {};
          if (caps.torch) setTorchSupported(true);
          if (caps.zoom) setZoomCaps({ min: caps.zoom.min || 1, max: caps.zoom.max || 5, step: caps.zoom.step || 0.1 });
        } catch { /* capabilities not exposed on some browsers — ignore */ }
        setScanHint("Point at a barcode — hold ~10cm away for best focus");
        return;
      } catch (err) {
        lastErr = err;
        // If it's an OverconstrainedError we retry with looser constraints
        if (err?.name !== "OverconstrainedError" && err?.name !== "NotReadableError") break;
      }
    }
    stopScan();
    const name = lastErr?.name || "";
    if (name === "NotAllowedError" || name === "SecurityError") {
      setCamError("Camera access was blocked. Allow camera permission in your browser, then tap Scan again — or enter the number below.");
    } else if (name === "NotFoundError" || name === "OverconstrainedError") {
      setCamError("No camera found — enter the barcode number instead.");
    } else {
      setCamError("Couldn't open the camera — enter the barcode number instead.");
    }
    setManual(true);
  };

  const toggleTorch = async () => {
    if (!trackRef.current) return;
    const next = !torchOn;
    try {
      await trackRef.current.applyConstraints({ advanced: [{ torch: next }] });
      setTorchOn(next);
    } catch { toast.error("Torch not supported on this camera"); }
  };

  const setZoomValue = async (v) => {
    if (!trackRef.current || !zoomCaps) return;
    setZoom(v);
    try { await trackRef.current.applyConstraints({ advanced: [{ zoom: v }] }); }
    catch { /* noop */ }
  };

  // Open the camera as soon as the Barcode tab is shown — athletes shouldn't
  // have to tap a second "Scan a barcode" button to get going.
  useEffect(() => {
    startScan();
    return () => stopScan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      {/* Video is ALWAYS mounted (hidden until scanning) so the ref is valid when
          ZXing attaches the stream — fixes the black camera window on Android. */}
      <div className={`force-dark relative bg-black border border-white/15 ${scanning ? "" : "hidden"}`} data-testid="barcode-camera">
        <video ref={videoRef} className="w-full aspect-video object-cover bg-black" muted playsInline autoPlay />
        {/* Reticle + animated red beam so athletes know to line the barcode up
            horizontally through the middle of the frame. */}
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="relative w-4/5 h-20 border-2 border-[#E2E30A]/80 rounded-sm overflow-hidden">
            <div className="absolute left-0 right-0 top-1/2 h-[2px] bg-[#FF3B30] shadow-[0_0_8px_#FF3B30] animate-pulse" />
          </div>
        </div>
        <div className="absolute top-2 left-2 flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#E2E30A] bg-black/60 px-2 py-1">
          <Camera className="h-3 w-3" /> {scanHint}
        </div>
        <div className="absolute top-2 right-2 flex items-center gap-1">
          {torchSupported && (
            <button data-testid="barcode-torch-btn" onClick={toggleTorch} title="Torch"
              className={`p-1.5 border ${torchOn ? "bg-[#FFB454] text-black border-[#FFB454]" : "bg-black/70 text-white border-white/20"}`}>
              <Zap className="h-4 w-4" />
            </button>
          )}
          <button data-testid="barcode-stop-btn" onClick={stopScan} className="p-1.5 bg-black/70 text-white border border-white/20"><X className="h-4 w-4" /></button>
        </div>
        {zoomCaps && zoomCaps.max > zoomCaps.min && (
          <div className="absolute bottom-2 left-2 right-2 flex items-center gap-2 bg-black/70 px-2 py-1.5">
            <span className="text-[9px] uppercase tracking-wider text-zinc-300">Zoom</span>
            <input data-testid="barcode-zoom" type="range" min={zoomCaps.min} max={zoomCaps.max} step={zoomCaps.step}
              value={zoom} onChange={(e) => setZoomValue(parseFloat(e.target.value))}
              className="flex-1 accent-[#E2E30A]" />
            <span className="text-[10px] font-mono text-[#E2E30A] tabular-nums w-8 text-right">{zoom.toFixed(1)}x</span>
          </div>
        )}
        {loading && <div className="absolute inset-0 flex items-center justify-center bg-black/50"><Loader2 className="h-6 w-6 animate-spin text-[#E2E30A]" /></div>}
      </div>

      {!scanning && (
        <button data-testid="barcode-scan-btn" onClick={startScan} disabled={loading}
          className="w-full flex flex-col items-center justify-center gap-2 border-2 border-dashed border-[#E2E30A]/50 bg-[#E2E30A]/[0.04] py-8 text-[#E2E30A] hover:bg-[#E2E30A]/[0.08] transition-colors">
          {loading ? <Loader2 className="h-8 w-8 animate-spin" /> : <Camera className="h-8 w-8" />}
          <span className="text-sm uppercase tracking-wide font-semibold">{loading ? "Opening camera…" : "Restart the scanner"}</span>
          <span className="text-[11px] text-zinc-400 normal-case tracking-normal">Tap to reopen your camera</span>
        </button>
      )}

      {camError && <p className="text-[11px] text-[#FF9500] mt-2" data-testid="barcode-cam-error">{camError}</p>}

      {!manual ? (
        <button data-testid="barcode-manual-toggle" onClick={() => setManual(true)}
          className="mt-3 w-full text-center text-[11px] uppercase tracking-wider text-zinc-400 hover:text-white transition-colors">
          Or enter the barcode number
        </button>
      ) : (
        <div className="mt-3">
          <div className="flex gap-2">
            <Input data-testid="barcode-input" value={code} onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && lookup(code.trim())} placeholder="Type barcode number" className="bg-black border-white/15 text-white" />
            <Button data-testid="barcode-lookup-btn" onClick={() => lookup(code.trim())} className="rounded-none bg-[#E2E30A] hover:bg-[#c9ca09] text-black font-semibold">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Look up"}
            </Button>
          </div>
        </div>
      )}

      {food && food.needsManualEntry && (
        <div data-testid="barcode-no-macros" className="mt-3 border border-[#FF9500]/40 bg-[#FF9500]/[0.06] p-3">
          <p className="text-[11px] uppercase tracking-wider text-[#FF9500] font-bold">
            No nutrition on file for this item
          </p>
          <p className="text-[12px] text-zinc-300 mt-1">
            <span className="text-white">{food.name}</span> — Open Food Facts doesn't have macros for this product. Add them once and we'll remember it for next time.
          </p>
          <button
            type="button"
            data-testid="barcode-enter-manual-btn"
            onClick={() => onManualFallback?.({ name: [food.brand, food.name].filter(Boolean).join(" · "), unit: food.unit || "g", code: food.code })}
            className="mt-2 w-full inline-flex items-center justify-center gap-2 bg-[#E2E30A] hover:bg-white text-black font-display font-black uppercase tracking-wider text-xs py-2.5 transition-colors">
            <PenLine className="h-4 w-4" /> Enter macros manually
          </button>
        </div>
      )}
      {food && !food.needsManualEntry && <PortionAdd food={{ ...food, source: "barcode" }} onPick={onPick} saving={saving} usual={usualFor(food)} onSaveUsual={onSaveUsual} onRemoveUsual={onRemoveUsual} />}
    </div>
  );
}

function CustomTab({ onPick, saving, initial }) {
  const [f, setF] = useState({ name: initial?.name || "", kcal: "", protein: "", carb: "", fat: "" });
  useEffect(() => { if (initial?.name) setF((prev) => ({ ...prev, name: initial.name })); }, [initial?.name]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const valid = f.name.trim() && Number(f.kcal) > 0;
  return (
    <div className="space-y-2">
      {initial?.name && (
        <p className="text-[10px] font-mono uppercase tracking-widest text-[#E2E30A]" data-testid="custom-prefill-hint">
          Pre-filled from barcode — just add the macros
        </p>
      )}
      <Input data-testid="custom-name" value={f.name} onChange={set("name")} placeholder="Food name" className="bg-black border-white/15 text-white" />
      <div className="grid grid-cols-4 gap-2">
        {[["kcal", "kcal"], ["protein", "P (g)"], ["carb", "C (g)"], ["fat", "F (g)"]].map(([k, lbl]) => (
          <div key={k}>
            <label className="text-[10px] uppercase text-zinc-400">{lbl}</label>
            <Input data-testid={`custom-${k}`} type="number" min="0" value={f[k]} onChange={set(k)} className="bg-black border-white/15 text-white" />
          </div>
        ))}
      </div>
      <Button data-testid="custom-add-btn" disabled={!valid || saving}
        onClick={() => onPick({ name: f.name.trim(), source: "custom", qty: 1, unit: "serving", kcal: Number(f.kcal), protein: Number(f.protein) || 0, carb: Number(f.carb) || 0, fat: Number(f.fat) || 0 })}
        className="w-full rounded-none bg-[#E2E30A] hover:bg-[#c9ca09] text-black text-xs uppercase font-semibold disabled:opacity-50">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add to log"}
      </Button>
    </div>
  );
}

function PhotoTab({ onPick, saving, onScan }) {
  const [busy, setBusy] = useState(false);
  const [est, setEst] = useState(null);
  // Preserved AI baseline (per serving) so portion chips can scale cleanly
  // even after the user manually tweaks a value.
  const [baseEst, setBaseEst] = useState(null);
  const [qty, setQty] = useState(1);
  const [photoPath, setPhotoPath] = useState(null);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setEst(null); setBaseEst(null); setQty(1);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/tracker/photo?estimate=1", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotoPath(data.photo_path);
      setEst(data.estimate);
      setBaseEst(data.estimate);
      // Save the AI estimate to scan history so the athlete can find it later
      // in Search even if they don't add-to-log right now.
      if (data?.estimate?.name) {
        onScan?.({ ...data.estimate, photo_path: data.photo_path });
      }
      toast.success("Photo analysed — adjust the portion, then add");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not analyse photo");
    } finally { setBusy(false); }
  };
  const setField = (k) => (e) => setEst({ ...est, [k]: e.target.value });
  // Scale every macro against the untouched baseline whenever the athlete
  // taps a portion chip — keeps the maths honest even after multiple tweaks.
  const applyPortion = (mult) => {
    if (!baseEst) return;
    setQty(mult);
    setEst({
      ...est,
      kcal: Math.round((Number(baseEst.kcal) || 0) * mult),
      protein: +((Number(baseEst.protein) || 0) * mult).toFixed(1),
      carb: +((Number(baseEst.carb) || 0) * mult).toFixed(1),
      fat: +((Number(baseEst.fat) || 0) * mult).toFixed(1),
    });
  };

  const chips = [
    { m: 0.5, label: "½" },
    { m: 0.75, label: "¾" },
    { m: 1, label: "1×" },
    { m: 1.5, label: "1½" },
    { m: 2, label: "2×" },
  ];

  return (
    <div className="space-y-3">
      {/* Two separate inputs: `capture` forces the camera on mobile, the other
          opens the gallery/file picker. One combined button only ever offered
          the picker on some devices. */}
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={handleFile} className="hidden" data-testid="photo-camera-input" />
      <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid="photo-file-input" />
      <div className="grid grid-cols-2 gap-2">
        <Button data-testid="photo-take-btn" onClick={() => cameraRef.current?.click()} disabled={busy}
          className="rounded-none bg-[#E2E30A] hover:bg-[#c9ca09] text-black text-xs uppercase font-semibold">
          {busy ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Analysing…</> : <><Camera className="h-4 w-4 mr-2" /> Take photo</>}
        </Button>
        <Button data-testid="photo-upload-btn" onClick={() => fileRef.current?.click()} disabled={busy}
          className="rounded-none bg-transparent border border-[#E2E30A]/60 hover:bg-[#E2E30A]/10 text-[#E2E30A] text-xs uppercase font-semibold">
          <Upload className="h-4 w-4 mr-2" /> Upload photo
        </Button>
      </div>
      <p className="text-[11px] text-zinc-400">AI estimates calories & macros for one serving. Adjust portion size below or fine-tune each value before adding.</p>
      {est && (
        <div className="border border-[#E2E30A]/40 p-3 bg-[#E2E30A]/[0.05] space-y-3">
          <Input data-testid="ai-name" value={est.name} onChange={setField("name")} className="bg-black border-white/15 text-white" />

          {/* Portion-size chips — one tap scales every macro against the
              AI baseline so athletes don't have to edit four numbers by hand
              when they had a bigger/smaller portion than a standard serve. */}
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-400 font-bold mb-1.5">Portion size</p>
            <div className="grid grid-cols-5 gap-1.5" data-testid="ai-portion-chips">
              {chips.map((c) => (
                <button key={c.m} type="button" data-testid={`ai-portion-${c.m}`}
                  onClick={() => applyPortion(c.m)}
                  className={`py-2 text-sm font-display transition-colors border ${qty === c.m
                    ? "bg-[#E2E30A] text-black border-[#E2E30A]"
                    : "bg-black text-zinc-300 border-white/15 hover:text-white hover:border-white/30"}`}>
                  {c.label}
                </button>
              ))}
            </div>
            {qty !== 1 && (
              <p className="text-[10px] font-mono text-[#E2E30A]/80 mt-1.5">Scaled from AI's 1× baseline</p>
            )}
          </div>

          <div className="grid grid-cols-4 gap-2">
            {[["kcal", "kcal"], ["protein", "P"], ["carb", "C"], ["fat", "F"]].map(([k, lbl]) => (
              <div key={k}>
                <label className="text-[10px] uppercase text-zinc-400">{lbl}</label>
                <Input data-testid={`ai-${k}`} type="number" min="0" value={est[k]} onChange={setField(k)} className="bg-black border-white/15 text-white" />
              </div>
            ))}
          </div>
          <p className="text-[10px] text-zinc-400">AI confidence: {est.confidence}</p>
          <Button data-testid="ai-add-btn" disabled={saving}
            onClick={() => onPick({ name: est.name, source: "ai", photo_path: photoPath, qty, unit: "serving", kcal: Number(est.kcal) || 0, protein: Number(est.protein) || 0, carb: Number(est.carb) || 0, fat: Number(est.fat) || 0 })}
            className="w-full rounded-none bg-[#E2E30A] hover:bg-[#c9ca09] text-black text-xs uppercase font-semibold">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : `Add ${qty !== 1 ? `${qty}× ` : ""}to log`}
          </Button>
        </div>
      )}
    </div>
  );
}
